/**
 * Button - shadcn/ui-style primitive tuned to the Consle brand.
 *
 * Variants are Consle-specific:
 *   - primary: Anchor Blue fill (used sparingly, one per view)
 *   - secondary: elevated surface with visible border
 *   - ghost: no border, no fill (inline actions)
 *   - destructive: error fill (for confirmations in modals)
 */

import { Slot } from '@radix-ui/react-slot';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from '~/lib/utils.ts';

export const buttonVariants = tv({
  base: [
    'inline-flex items-center justify-center gap-2 cursor-pointer',
    'rounded-[4px] text-sm font-medium',
    'transition-colors duration-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-canvas)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.98]',
  ],
  variants: {
    variant: {
      primary:
        'bg-[color:var(--accent)] text-[color:var(--color-consle-signal)] shadow-[var(--shadow-sm)] hover:bg-[color:var(--accent-hover)]',
      secondary:
        'border border-[color:var(--border-strong)] bg-[color:var(--bg-elevated)] text-[color:var(--fg-primary)] shadow-[var(--shadow-sm)] hover:bg-[color:var(--bg-active)]',
      ghost:
        'bg-transparent text-[color:var(--fg-secondary)] hover:bg-[color:var(--bg-hover)] hover:text-[color:var(--fg-primary)]',
      destructive:
        'bg-[color:var(--color-error)] text-[color:var(--color-consle-signal)] shadow-[var(--shadow-sm)] hover:opacity-90',
    },
    size: {
      sm: 'h-7 px-3 text-xs',
      md: 'h-8 px-4',
      lg: 'h-10 px-5',
      xl: 'h-12 px-6',
    },
  },
  defaultVariants: {
    variant: 'secondary',
    size: 'md',
  },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  readonly asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
) {
  const Component = asChild ? Slot : 'button';
  return (
    <Component ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  );
});
